/* eslint-disable react-refresh/only-export-components */
import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { Badge } from '@/components/ui/badge';
import {
  useCertificationStandardOptions,
  useCollaborationStatusOptions,
  useNetworkCompanyOptions,
} from '@/hooks/useDropdowns';
import { useTranslator } from '@/hooks/useTranslator';
import { useSessionStore } from '@/store/sessionStore';
import { CertDrilldownTable } from '../../../../../-components/certificate-compliance/CertDrilldownTable';
import {
  buildCertDrilldownFilter,
  parseFilterParam,
} from '../../../../../-utils/drilldownUtils';

export const Route = createFileRoute(
  '/(client)/report/(pages)/(drilldown)/certificate-compliance/drilldown/$chartType/',
)({
  // TanStack Router auto-parses JSON query params, so `filter` may arrive as
  // an already-parsed object (when the URL was built with URLSearchParams) or
  // as a raw string (when navigate() double-encodes it). Accept both.
  validateSearch: z.object({
    filter: z.union([z.string(), z.record(z.unknown())]).optional(),
  }),
  component: CertDrilldownPage,
});

function CertDrilldownPage() {
  const { getTranslation: t } = useTranslator({ page: 'certCompliance' });
  const { chartType } = useParams({ strict: false }) as { chartType: string };
  const search = (useSearch({ strict: false }) ?? {}) as {
    filter?: string | Record<string, unknown>;
  };
  const companyId = useSessionStore(s => s.user?.companyId ?? '');

  const filterObj = parseFilterParam(search.filter);
  const drilldownFilter = buildCertDrilldownFilter(filterObj);

  // Load options to resolve IDs → display labels (matches old frontend behaviour)
  const { data: supplierOptions = [] } = useNetworkCompanyOptions();
  const { data: collabStatusOptions = [] } = useCollaborationStatusOptions();
  const { data: standardsRaw = [] } = useCertificationStandardOptions();
  const standardOptions = (standardsRaw as { id: string; name: string }[]).map(
    s => ({ key: s.id, label: s.name }),
  );

  const resolveIds = (
    ids: string[],
    options: { key: string; label: string }[],
  ) => ids.map(id => options.find(o => o.key === id)?.label ?? id);

  const buildFilterChips = (): string[] => {
    const chips: string[] = [];

    const expirationStatus = drilldownFilter.expirationStatus as
      | string
      | undefined;
    if (expirationStatus)
      chips.push(
        t(`drilldown.expirationStatus.${expirationStatus}`) || expirationStatus,
      );
    if (
      drilldownFilter.depthLevel !== undefined &&
      drilldownFilter.depthLevel !== ''
    ) {
      chips.push(
        t('drilldown.chips.depth').replace(
          '{{value}}',
          String(drilldownFilter.depthLevel),
        ),
      );
    }
    if (drilldownFilter.expirationDate)
      chips.push(String(drilldownFilter.expirationDate));
    const status = drilldownFilter.status as string | undefined;
    if (status) chips.push(t(`drilldown.claimStatus.${status}`) || status);

    const supplierIds =
      (drilldownFilter.supplierCompanyIds as string[] | undefined) ?? [];
    chips.push(...resolveIds(supplierIds, supplierOptions));

    const collabIds =
      (drilldownFilter.collaborationStatusIds as string[] | undefined) ?? [];
    chips.push(...resolveIds(collabIds, collabStatusOptions));

    const standardIds =
      (drilldownFilter.standardIds as string[] | undefined) ?? [];
    chips.push(...resolveIds(standardIds, standardOptions));

    return chips;
  };

  const filterChips = buildFilterChips();
  const chartTitle = t(`drilldown.chartType.${chartType}`) || chartType;

  return (
    <PageContainer className='flex flex-col overflow-hidden'>
      <div className='shrink-0 border-b border-border/60 bg-background px-4 py-4'>
        <h1 className='text-sm font-semibold text-foreground leading-tight'>
          {chartTitle}
        </h1>
        <p className='text-sm text-muted-foreground mt-0.5'>
          {t('drilldown.description')}
        </p>
        {filterChips.length > 0 && (
          <div className='flex flex-wrap gap-1.5 mt-2'>
            {filterChips.map(chip => (
              <Badge
                key={chip}
                variant='secondary'
                className='rounded-md text-xs font-normal'
              >
                {chip}
              </Badge>
            ))}
          </div>
        )}
      </div>
      <div className='flex flex-col flex-1 min-h-0 overflow-hidden'>
        <CertDrilldownTable companyId={companyId} filter={drilldownFilter} />
      </div>
    </PageContainer>
  );
}
